import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import * as api from "../service/testimonialsService";
import type { TestimonialsSection, TestimonialItem } from "../types";

interface TestimonialsState {
  section: TestimonialsSection | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: TestimonialsState = { section: null, loading: false, saving: false, error: null };

export const fetchTestimonials = createAsyncThunk("cms/testimonials/fetch", api.getTestimonials);

export const saveTestimonialsSection = createAsyncThunk(
  "cms/testimonials/save",
  (payload: Pick<TestimonialsSection, "badgeText" | "headingLine1" | "headingLine2" | "subtitle">) =>
    api.updateTestimonialsSection(payload)
);

export const addTestimonialItem = createAsyncThunk(
  "cms/testimonials/add",
  (payload: Omit<TestimonialItem, "id">) => api.addTestimonial(payload)
);

export const editTestimonialItem = createAsyncThunk(
  "cms/testimonials/edit",
  ({ id, payload }: { id: number; payload: Omit<TestimonialItem, "id"> }) => api.updateTestimonial(id, payload)
);

export const removeTestimonialItem = createAsyncThunk(
  "cms/testimonials/remove",
  (id: number) => api.removeTestimonial(id)
);

const testimonialsSlice = createSlice({
  name: "cmsTestimonials",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTestimonials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTestimonials.fulfilled, (state, action: PayloadAction<TestimonialsSection>) => {
        state.loading = false;
        state.section = action.payload;
      })
      .addCase(fetchTestimonials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load testimonials section.";
      })
      .addCase(saveTestimonialsSection.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveTestimonialsSection.fulfilled, (state, action: PayloadAction<TestimonialsSection>) => {
        state.saving = false;
        if (state.section) {
          state.section.badgeText = action.payload.badgeText;
          state.section.headingLine1 = action.payload.headingLine1;
          state.section.headingLine2 = action.payload.headingLine2;
          state.section.subtitle = action.payload.subtitle;
        } else {
          state.section = action.payload;
        }
      })
      .addCase(saveTestimonialsSection.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? "Failed to save section.";
      })
      .addCase(addTestimonialItem.fulfilled, (state, action: PayloadAction<TestimonialItem>) => {
        if (!state.section) return;
        state.section.testimonials.push(action.payload);
      })
      .addCase(editTestimonialItem.fulfilled, (state, action: PayloadAction<TestimonialItem>) => {
        if (!state.section) return;
        const idx = state.section.testimonials.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) {
          state.section.testimonials[idx] = action.payload;
        }
      })
      .addCase(removeTestimonialItem.fulfilled, (state, action: PayloadAction<number>) => {
        if (!state.section) return;
        state.section.testimonials = state.section.testimonials.filter((t) => t.id !== action.payload);
      });
  },
});

export default testimonialsSlice.reducer;